import amplitude, { AmplitudeClient } from 'amplitude-js';

// User Agent parser from Amplitude client (installed as separate package)
// Because UA data is not accessible through the Amplitude client
import capitalize from 'lodash/capitalize';
import UAParser from 'ua-parser-js';

import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import { currentTeamId } from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentTeam';
import {
  Team,
  Member,
} from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentUserTeamsQuery';
import I18n from '@wetransfer/transfer/modules/i18n';
import Translations from '@wetransfer/transfer/modules/i18n/dictionary';

export interface USER_PROPERTIES {
  Coupon?: string;
}

let amplitudeAnalyticsInitialised = false;

export const setIsInitialised = (): void => {
  amplitudeAnalyticsInitialised = true;
};

export const getIsInitialised = (): boolean => {
  return amplitudeAnalyticsInitialised;
};

const getAmplitude = (): AmplitudeClient => {
  return amplitude.getInstance();
};

export const getAmplitudeDeviceId = (): string | undefined => {
  return getAmplitude().getDeviceId();
};

export const getAmplitudeUserId = (): string | undefined => {
  return getAmplitude().getUserId();
};

export const setAmplitudeUserProperties = (props: USER_PROPERTIES): void => {
  getAmplitude().setUserProperties({
    ...props,
  });
};

interface AmplitudeDeviceInfo {
  platform?: string;
  deviceType?: string;
  deviceFamily?: string;
}

export const getAmplitudeDeviceInfo = (): AmplitudeDeviceInfo => {
  const parser = new UAParser();
  const deviceInfo = parser.getDevice();

  return {
    deviceType: `${deviceInfo.vendor || ''} ${deviceInfo.model || ''}`.trim(),
    deviceFamily: deviceInfo.model || '',
    platform: getAmplitude().options.platform,
  };
};

export const setAmplitudeDeviceId = (deviceId: string): void => {
  getAmplitude().setDeviceId(deviceId);
};

type AmplitudeKeyValue = Record<string, string | number>;

type AdditionalProperties = Record<string, string | number>;

const getTierForTracking = (
  tier: string
): 'Free' | 'Pro_2022' | 'Plus' | 'Premium_2022' => {
  if (tier === PLAN_TIER.FREE) {
    return 'Free';
  }

  if (tier === PLAN_TIER.PLUS) {
    return 'Plus';
  }

  if (tier === PLAN_TIER.PRO_2022) {
    return 'Pro_2022';
  }

  if (tier === PLAN_TIER.PREMIUM_2022) {
    return 'Premium_2022';
  }
};

export interface AmplitudeExtendableEvent {
  action: string;
  groups?: AmplitudeKeyValue;
  additionalProperties?: AdditionalProperties;
}

export const trackAmplitudeEvent = ({
  action,
  groups,
  additionalProperties,
}: AmplitudeExtendableEvent): void => {
  if (!getAmplitude() || !amplitudeAnalyticsInitialised) {
    return;
  }

  if (!action) {
    throw new Error('"Action" is required');
  }

  const workspaceId = currentTeamId();

  if (groups || workspaceId) {
    getAmplitude().logEventWithGroups(action, additionalProperties, {
      ...groups,
      'Workspace ID': workspaceId,
    });

    return;
  }

  if (additionalProperties?.couponCode) {
    getAmplitude().setUserProperties({
      Coupon: additionalProperties.couponCode,
    });
  }

  getAmplitude().logEvent(action, additionalProperties);
};

const setCurrentWorkspaceRoleUserProperty = (
  currentTeam: Team,
  userEmail: string
): void => {
  const currentWorkspaceUserDetails = currentTeam.members.find(
    (member: Member) => {
      return member.user.email === userEmail;
    }
  );

  getAmplitude().setUserProperties({
    'Current Workspace Role': capitalize(currentWorkspaceUserDetails?.role),
  });
};

export const initAmplitude = ({
  apiKey,
  userId,
  planTier,
  isInternal,
  currentTeam,
  userEmail,
}: {
  apiKey?: string;
  userId: string;
  planTier: PLAN_TIER;
  isInternal: boolean;
  currentTeam: Team;
  userEmail: string;
}): void => {
  if (!apiKey) {
    return;
  }

  getAmplitude().init(apiKey);

  if (userId) {
    getAmplitude().setUserId(userId);
  }

  if (planTier) {
    getAmplitude().setUserProperties({
      'Subscription Tier': getTierForTracking(planTier),
    });
  }

  if (currentTeam) {
    setCurrentWorkspaceRoleUserProperty(currentTeam, userEmail);
  }

  const locales = Translations.locales;
  const nativeLanguage = locales[I18n.locale as keyof typeof locales]?.[0]; // 0 = Native language,

  getAmplitude().setUserProperties({
    'Is Internal': isInternal,
    'WT Language': nativeLanguage,
  });

  setIsInitialised();
};

export const implementTransferGroup = ({
  transferId,
  fileSizeInBytes,
  userUploadRole,
}: {
  transferId: string;
  fileSizeInBytes: number;
  userUploadRole: 'Sender' | 'Uploader';
}): void => {
  const workspaceId = currentTeamId();

  const workspaceGroupIdentify = new amplitude.Identify().set(
    'File Size in Bytes',
    fileSizeInBytes
  );

  const transferGroupIdentify = new amplitude.Identify()
    .set('File Size in Bytes', fileSizeInBytes)
    .set('Created By', userUploadRole);

  getAmplitude().groupIdentify(
    'Transfer ID',
    transferId,
    transferGroupIdentify
  );

  getAmplitude().groupIdentify(
    'Workspace ID',
    workspaceId,
    workspaceGroupIdentify
  );
};

export const implementWorkspaceGroup = ({
  userEmail,
  userPlanTier,
  userStorageLimit,
  currentTeam,
  memberships,
  currentStorage,
}: {
  userEmail: string;
  userPlanTier: string;
  userStorageLimit: number;
  currentTeam: Team;
  memberships: Member[];
  currentStorage: number;
}): void => {
  getAmplitude().setUserProperties({
    'Workspace Count': memberships?.length,
  });

  if (currentTeam) {
    setCurrentWorkspaceRoleUserProperty(currentTeam, userEmail);

    const groupIdentify = new amplitude.Identify()
      .set('Subscription Tier (Workspace)', userPlanTier)
      .set('Transfer Storage Quota in Bytes', userStorageLimit - currentStorage)
      .set('Transfer Storage Used in Bytes', currentStorage)
      .set('Member Count', currentTeam.members?.length);

    getAmplitude().groupIdentify('Workspace ID', currentTeam.id, groupIdentify);
  }
};

export const removeUserId = (): void => {
  getAmplitude().setUserId(null);
};

export const stopAmplitudeTracking = (): void => {
  amplitude.getInstance().setOptOut(true);
};

export const startAmplitudeTracking = (): void => {
  amplitude.getInstance().setOptOut(false);
};
